<template>
  <div
    id="creatives-preview"
    class="d-flex flex-column overflow-hidden"
  >
    <header
      id="creatives-preview-header"
      class="bg-secondary d-flex align-items-center justify-content-center"
      style="height: 58px;"
    >
      <div
        class="d-flex align-items-center"
      >
        <b-button
          variant="tertiary"
          class="d-flex align-items-center"
          data-test="previous-creative-btn"
          @click="updateCarouselIndex(-1)"
          :disabled="!carouselPrevButtonEnabled"
        >
          <i class="h5 mb-0 fal fa-chevron-left" />
        </b-button>
        <h5
          id="creative-template-name"
          class="mb-0"
        >
          <strong>
            {{ selectedCreativeTemplateName }}
          </strong>
        </h5>
        <b-button
          variant="tertiary"
          class="d-flex align-items-center"
          data-test="next-creative-btn"
          @click="updateCarouselIndex(+1)"
          :disabled="!carouselNextButtonEnabled"
        >
          <i class="h5 mb-0 fal fa-chevron-right" />
        </b-button>
      </div>
      <b-button
        v-if="GET_IS_MANAGED_CAMPAIGN && GET_HAS_CAMPAIGN_ACCESS"
        id="manage-trackers-btn"
        variant="secondary"
        size="sm"
        @click="showManageTrackers()"
        data-test="manage-trackers"
      >
        Manage Trackers
      </b-button>
    </header>
    <div
      v-if="GET_HAS_CAMPAIGN_ACCESS && shouldDisplayCreativeTemplateState"
      class="creative-state-container d-flex align-items-center justify-content-center"
    >
      <play-pause-button
        @updateState="toggleCreativePausedOverlay"
      />
    </div>
    <div
      id="carousel-container"
      ref="carousel-container"
      class="w-100 flex-grow-1 p-5 overflow-hidden"
      @click="setIsSidebarOpen(isAddingNewAnnotation)"
    >
      <ul
        id="carousel"
        ref="carousel-slider"
        class="list-unstyled d-flex m-0 p-0 h-100 align-items-center"
      >
        <li
          v-for="(creativeTemplate, index) in GET_ACTIVE_CREATIVE_TEMPLATES"
          :key="creativeTemplate.id"
          ref="creatives-carousel-slides"
          class="carousel__slide h-100 w-100 flex-shrink-0 d-flex align-items-center justify-content-center"
        >
          <div
            v-if="isIFrameLoading && isLoadingArticles"
            class="loader"
          />
          <div
            v-show="!isIFrameLoading && selectedArticleUuid"
            :ref="`iframe-container-${creativeTemplate.creativeId}`"
            class="iframe-container"
            :style="{ width: getCreativeTemplateWidth(index), height: getCreativeTemplateHeight(index) }"
          >
            <content-annotations
              v-if="allowAnnotations"
              :style="{ width: getCreativeTemplateWidth(index), height: getCreativeTemplateHeight(index) }"
            />
            <iframe
              :key="creativeTemplateIFrame"
              :ref="`preview-iframe-${creativeTemplate.creativeId}`"
              :id="`preview-iframe-${creativeTemplate.creativeId}`"
              class="preview-iframe"
              title="Content Preview"
              :srcdoc="getSourceDocument(creativeTemplate)"
              :width="getCreativeTemplateWidth(index)"
              :height="getCreativeTemplateHeight(index)"
              scrolling="no"
              marginwidth="0"
              marginheight="0"
              hspace="0"
              vspace="0"
              frameborder="0"
              @load="handleIFrameLoad"
            />
            <div
              :ref="`creative-paused-overlay-${index}`"
            />
          </div>
        </li>
      </ul>
    </div>
  </div>
</template>
<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import * as campaignTypes from '@/modules/campaigns/store/types';
import * as contentTypes from '@/modules/content/store/types';

import PlayPauseButton from '@/modules/content/components/CreativesPage/PlayPauseButton';
import ContentAnnotations from '@/modules/content/components/ContentAnnotations';

export default {
  name: 'CreativesPreview',
  components: {
    PlayPauseButton,
    ContentAnnotations,
  },
  props: {
    isSidebarOpen: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      resizeObserver: null,
      carouselContainerWidth: 0,
      carouselContainerHeight: 0,
      carouselIndex: 0,
      isIFrameLoading: false,
      areInteractionsTimedOut: false,
      creativeTemplateIFrame: 0,
    };
  },
  computed: {
    ...mapState('content', [
      'isLoadingArticles',
      'isSidebarInputFocused',
      'timestamp',
      'creativeTemplates',
      'creativesSizes',
      'selectedArticleUuid',
      'selectedCreativeId',
      'allowAnnotations',
      'isAddingNewAnnotation',
    ]),
    ...mapGetters('content', [
      contentTypes.GET_ACTIVE_CREATIVES,
      contentTypes.GET_ARTICLE_BY_UUID,
      contentTypes.GET_CONTENT_ITEM_ACTIVE_STATE,
      contentTypes.GET_ACTIVE_CREATIVE_TEMPLATES,
    ]),
    ...mapGetters('campaigns', [
      campaignTypes.GET_HAS_CAMPAIGN_ACCESS,
      campaignTypes.GET_IS_MANAGED_CAMPAIGN,
      campaignTypes.GET_IS_CAMPAIGN_LIVE,
      campaignTypes.GET_IS_CAMPAIGN_DORMANT,
      campaignTypes.GET_IS_CAMPAIGN_PAUSED,
    ]),
    selectedCreativeTemplate() {
      return this.GET_ACTIVE_CREATIVE_TEMPLATES[this.carouselIndex] || {};
    },
    selectedCreativeTemplateName() {
      return this.selectedCreativeTemplate?.name || '';
    },
    carouselPrevButtonEnabled() {
      return !this.areInteractionsTimedOut
      && this.carouselIndex > 0;
    },
    carouselNextButtonEnabled() {
      return !this.areInteractionsTimedOut
      && this.carouselIndex < this.GET_ACTIVE_CREATIVE_TEMPLATES.length - 1;
    },
    slides() {
      return this.$refs['creatives-carousel-slides'];
    },
    selectedArticle() {
      return this.GET_ARTICLE_BY_UUID(this.selectedArticleUuid) || {};
    },
    shouldDisplayCreativeTemplateState() {
      return (this.GET_IS_CAMPAIGN_LIVE
        || this.GET_IS_CAMPAIGN_DORMANT
        || this.GET_IS_CAMPAIGN_PAUSED)
        && (['PUBLISHED', 'PAUSED'].includes(this.selectedArticle?.state));
    },
    isActiveCreativeTemplate() {
      return this.GET_CONTENT_ITEM_ACTIVE_STATE(this.selectedArticleUuid, this.selectedCreativeTemplate.creativeId);
    },
    shouldDisplayPausedOverlay() {
      return !this.isActiveCreativeTemplate || this.selectedArticle?.state === 'PAUSED';
    },
    isKeyboardNavigationBlocked() {
      return this.isSidebarInputFocused;
    },
  },
  methods: {
    ...mapActions('content', [
      contentTypes.SET_SELECTED_CREATIVE_ID,
    ]),
    getSourceDocument(creativeTemplate) {
      return `\x3cscript src="${process.env.VUE_APP_RENDER_API}/preview/creative/${creativeTemplate.creativeId}/key/${creativeTemplate.creativeKey}/acid/${this.selectedArticleUuid}?${this.timestamp}"\x3e\x3c/script\x3e`;
    },
    updateCarouselIndex(step) {
      if (this.areInteractionsTimedOut) return;
      if (step === -1 && !this.carouselPrevButtonEnabled) return;
      if (step === +1 && !this.carouselNextButtonEnabled) return;
      const slides = this.$refs['creatives-carousel-slides'];

      const outgoingslide = slides[this.carouselIndex];
      outgoingslide.classList.add('fadeOut');

      const incomingSlide = slides[this.carouselIndex + step];
      incomingSlide.classList.add('fadeIn');

      outgoingslide.addEventListener('animationend', () => {
        outgoingslide.classList.remove('current-slide');
        outgoingslide.classList.remove('fadeOut');
      }, { once: true });

      this.carouselIndex += step;

      incomingSlide.addEventListener('animationend', () => {
        incomingSlide.classList.add('current-slide');
        incomingSlide.classList.remove('fadeIn');
      }, { once: true });
      this.SET_SELECTED_CREATIVE_ID(this.selectedCreativeTemplate.creativeId);
      this.setCreativeTemplateScaling();
      this.toggleCreativePausedOverlay();
      slides.forEach((slide) => {
        slide.style.transform = `translateX(${this.carouselIndex * -this.carouselContainerWidth}px)`;
      });

      this.areInteractionsTimedOut = true;
      setTimeout(() => {
        this.areInteractionsTimedOut = false;
      }, 100);
    },
    setCreativeTemplateScaling() {
      const iFrameContainer = this.$refs[`iframe-container-${this.selectedCreativeId}`][0];
      let scaleAmount = 1;
      if (this.carouselContainerWidth < this.selectedCreativeTemplate.width) {
        scaleAmount = this.carouselContainerWidth / this.selectedCreativeTemplate.width;
        iFrameContainer.style.setProperty('transform-origin', 'center center');
      } else if (this.carouselContainerHeight < this.selectedCreativeTemplate.height) {
        scaleAmount = this.carouselContainerHeight / this.selectedCreativeTemplate.height;
        iFrameContainer.style.setProperty('transform-origin', 'center center');
      } else iFrameContainer.style.setProperty('transform-origin', 'center center');
      iFrameContainer.style.setProperty('transform', `scale(${scaleAmount})`);
    },
    handleIFrameLoad() {
      if (!this.selectedArticleUuid) return;
      this.isIFrameLoading = true;
      const iFrameDocument = document.getElementById(`preview-iframe-${this.selectedCreativeId}`).contentWindow.document;
      this.setCreativeTemplateScaling();
      this.SET_SELECTED_CREATIVE_ID(this.selectedCreativeTemplate.creativeId);
      this.toggleCreativePausedOverlay();
      if (iFrameDocument?.querySelector('body')?.hasChildNodes()) this.isIFrameLoading = false;
    },
    getCreativeTemplateWidth(index) {
      return `${this.GET_ACTIVE_CREATIVE_TEMPLATES[index].width}px`;
    },
    getCreativeTemplateHeight(index) {
      return `${this.GET_ACTIVE_CREATIVE_TEMPLATES[index].height}px`;
    },
    toggleCreativePausedOverlay() {
      const creativePausedOverlayClassList = this.$refs[`creative-paused-overlay-${this.carouselIndex}`][0].classList;
      if (!this.shouldDisplayPausedOverlay && [...creativePausedOverlayClassList].includes('creative-paused')) {
        return creativePausedOverlayClassList.remove('creative-paused');
      }
      if (this.shouldDisplayPausedOverlay) creativePausedOverlayClassList.add('creative-paused');
    },
    handleKeydown(event) {
      if (this.isKeyboardNavigationBlocked) return;
      if (event.key === 'ArrowRight') {
        return this.updateCarouselIndex(+1);
      }
      if (event.key === 'ArrowLeft') {
        return this.updateCarouselIndex(-1);
      }
    },
    async attachResizeObserver() {
      this.detachResizeObserver();
      this.resizeObserver = new ResizeObserver(() => {
        const rootElement = document.querySelector(':root');

        const carouselContainerComputedStyles = window.getComputedStyle(this.$refs['carousel-container']);
        const paddingX = parseInt(carouselContainerComputedStyles['padding-left'], 10) + parseInt(carouselContainerComputedStyles['padding-right'], 10);
        this.carouselContainerWidth = this.$refs['carousel-container'].clientWidth - paddingX;
        const paddingY = parseInt(carouselContainerComputedStyles['padding-top'], 10) + parseInt(carouselContainerComputedStyles['padding-bottom'], 10);
        this.carouselContainerHeight = this.$refs['carousel-container'].clientHeight - paddingY;

        this.setCreativeTemplateScaling();
        this.slides.forEach((slide) => {
          slide.style.setProperty('transform', `translateX(${this.carouselIndex * -this.carouselContainerWidth}px)`);
        });
        rootElement.style.setProperty('--carousel-container-width', `${this.$refs['carousel-container'].clientWidth}px`);
      });

      this.resizeObserver.observe(this.$refs['carousel-container']);
    },
    detachResizeObserver() {
      if (this.resizeObserver instanceof ResizeObserver) {
        this.resizeObserver.unobserve(this.$refs['carousel-container']);
      }
    },
    showManageTrackers() {
      this.$emit('showManageTrackers');
    },
    setIsSidebarOpen(isOpen) {
      this.$emit('setIsSidebarOpen', !!isOpen);
    },
  },
  async mounted() {
    this.isIFrameLoading = true;
    const foundTemplate = this.GET_ACTIVE_CREATIVE_TEMPLATES.find((template) => template.creativeId === this.selectedCreativeId);
    this.carouselIndex = foundTemplate ? this.GET_ACTIVE_CREATIVE_TEMPLATES.indexOf(foundTemplate) : 0;
    window.addEventListener('keydown', this.handleKeydown);
    await this.attachResizeObserver();
    await this.$refs['creatives-carousel-slides'][this.carouselIndex].classList.add('current-slide');
  },
  beforeDestroy() {
    window.removeEventListener('keydown', this.handleKeydown);
    this.detachResizeObserver();
  },
};
</script>
<style scoped lang="scss">
#creatives-preview-header {
  position: relative;
}
#manage-trackers-btn {
  position: absolute;
  right: 24px;
}
#creative-template-name {
  min-width: 240px;
  text-align: center;
}
.creative-state-container {
  position: relative;
}
#carousel-container {
  width: var(--carousel-container-width, auto);
  #carousel {
    width: inherit;
    .carousel__slide {
      width: inherit;
      opacity: 0;
      pointer-events: none;
    }
    .current-slide {
      opacity: 1;
      pointer-events: visible;
    }
    .fadeOut {
      animation-name: fadeOut;
      animation-duration: .5s;
      animation-fill-mode: forwards;
      transition: transform .5s ease-in-out;
    }
    .fadeIn {
      animation-name: fadeIn;
      animation-duration: .5s;
      animation-fill-mode: forwards;
      transition: transform .5s ease-in-out;
    }
  }
}
@keyframes fadeOut {
  0% {
    opacity: 1;
  }
  40% {
    opacity: 0;
  }
  100% {
    opacity: 0;
  }
}
@keyframes fadeIn {
  0% {
    opacity: 0;
  }
  60% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
}
.iframe-container {
  position: relative;
}
.creative-paused {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: repeating-linear-gradient(15deg, #ddd, #ddd 70px, #aaa 70px, #aaa 140px);
  opacity: .7;
}
</style>
